export const SECTORS_MIN = 1
export const SECTORS_MAX = 256
export const SECTOR_SIZE_CM_MIN = 1
export const SECTOR_SIZE_CM_MAX = 1000
export const SECTOR_SIZE_M_MIN = 0.01
export const SECTOR_SIZE_M_MAX = 10
export const PLATFORM_SIDE_MAX_M = 256
export const HEIGHT_MIN = -20
export const HEIGHT_MAX = 20
export const FACE_GRID = 8
/** Max |depth| (sector steps): + dig inward, − protrude outward. */
export const OPENING_DEPTH_MAX = SECTORS_MAX
export const OPENING_DEPTH_MIN = -SECTORS_MAX
export const OPENING_STEP = 1
/** Fixed sector size for platform form (no UI input). */
export const DEFAULT_SECTOR_SIZE = 1
/** Floor foundation depth in sector units; minimum when span is small. */
export const FLOOR_FOUNDATION_SECTORS = 8
/** Floor foundation depth as multiple of platform span (extends below y=0). */
export const FLOOR_FOUNDATION_SPAN_RATIO = 2

export type PlatformUnit = "cm" | "m"
export type ScenePlatform = {
  sectors_x: number
  sectors_z: number
  sector_size: number
  unit: PlatformUnit
}
export type SceneCell = { x: number; z: number; height: number }
export type SceneOpening = { face_key: string; u: number; v: number; depth: number }
export type FaceTile = { faceKey: string; u: number; v: number }
export type CellFace = "top" | "bottom" | "north" | "south" | "east" | "west"
export type WallSide = "north" | "east" | "south" | "west"
export type WallLayoutPreset = "none" | "one" | "farTwo" | "three" | "box"
export type PlatformErrorCode = "sectors_x" | "sectors_z" | "unit" | "sector_size" | "platform_side" | "height"

/** Create-dialog wall outline presets (top-down; far edges = south + east from default NW camera). */
export const WALL_LAYOUT_PRESETS: readonly WallLayoutPreset[] = ["none", "one", "farTwo", "three", "box"]

export const DEFAULT_WALL_LAYOUT_PRESET: WallLayoutPreset = "none"
/** Sector-unit height for create-dialog perimeter walls. */
export const DEFAULT_WALL_HEIGHT = 6

export function wallSidesForPreset(preset: WallLayoutPreset): ReadonlySet<WallSide> {
  switch (preset) {
    case "one":
      return new Set(["south"])
    case "farTwo":
      return new Set(["south", "east"])
    case "three":
      return new Set(["south", "east", "west"])
    case "box":
      return new Set(["north", "east", "south", "west"])
    case "none":
    default:
      return new Set()
  }
}

const CELL_FACE_RE = /^cell:(\d+):(\d+):(top|bottom|north|south|east|west)$/
const OBJECT_FACE_RE = /^obj:([A-Za-z0-9_-]{1,64}):(px|nx|py|ny|pz|nz)$/

export function emptyPlatform(): ScenePlatform {
  return { sectors_x: 32, sectors_z: 32, sector_size: DEFAULT_SECTOR_SIZE, unit: "m" }
}

export function platformIsSet(platform: ScenePlatform | null | undefined): platform is ScenePlatform {
  return Boolean(platform && platform.sectors_x > 0 && platform.sectors_z > 0 && platform.sector_size > 0)
}

export function sectorSizeMeters(platform: Pick<ScenePlatform, "sector_size" | "unit">): number {
  return platform.unit === "cm" ? platform.sector_size / 100 : platform.sector_size
}

/** World Y extent of the floor foundation parallelepiped (extends below y=0). */
export function cellFloorDepth(sectorM: number, spanMeters: number): number {
  const span = Number.isFinite(spanMeters) && spanMeters > 0 ? spanMeters : sectorM
  return Math.max(span * FLOOR_FOUNDATION_SPAN_RATIO, sectorM * FLOOR_FOUNDATION_SECTORS, 0.02)
}

export function cellWorldHeight(height: number, sectorM: number, spanMeters = sectorM): number {
  if (height <= 0) return cellFloorDepth(sectorM, spanMeters)
  return Math.max(height, 0) * sectorM
}

/** World Y of the bottom of the cell mesh. Floor: −depth; walls: terrace at y=0. */
export function cellStackBaseY(height: number, sectorM: number, spanMeters = sectorM): number {
  if (height <= 0) return -cellFloorDepth(sectorM, spanMeters)
  return 0
}

/** Vertical-face selection grid: one column of sector-sized squares that fit in total. */
export function faceTileGrid(total: number, sectorM: number) {
  const step = sectorM
  const rows = Math.max(0, Math.floor((Math.max(total, 0) + 1e-9) / step))
  return { cols: 1, rows, step }
}

/** Vertical tiles on one wall column. Sector size cancels, so this is floor(height). */
export function wallFaceRowCount(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sector_size" | "unit">,
): number {
  const parsed = parseCellFaceKey(faceKey)
  if (!parsed || parsed.face === "top" || parsed.face === "bottom") return 0
  const height = cells.find((cell) => cell.x === parsed.x && cell.z === parsed.z)?.height ?? 0
  if (height <= 0) return 0
  return faceTileGrid(height * sectorSizeMeters(platform), sectorSizeMeters(platform)).rows
}

type WallPaintableFace = {
  face_key: string
  origin: "upload" | "library" | "generated"
  status: "pending" | "ready" | "failed"
  prompt?: string | null
  error_message?: string | null
  url?: string | null
  thumb_url?: string | null
  download_url?: string | null
  solid_color?: string | null
  tile_colors?: Record<string, string> | null
}

function wallTileKey(u: number, v: number): string {
  return `${u}:${v}`
}

/**
 * Paint or clear selected wall rows. A full column becomes `solid_color` with empty
 * `tile_colors` (and drops the image, matching a column fill). A partial column expands
 * an existing column color onto every row, then writes only the selected rows.
 */
export function applyWallTileColors<T extends WallPaintableFace>(
  faces: ReadonlyArray<T>,
  tiles: ReadonlyArray<FaceTile>,
  hex: string | null,
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sector_size" | "unit">,
): T[] {
  const selectedByFace = new Map<string, Set<string>>()
  for (const tile of tiles) {
    const parsed = parseCellFaceKey(tile.faceKey)
    if (!parsed || parsed.face === "top" || parsed.face === "bottom") continue
    let selected = selectedByFace.get(tile.faceKey)
    if (!selected) {
      selected = new Set()
      selectedByFace.set(tile.faceKey, selected)
    }
    selected.add(wallTileKey(tile.u, tile.v))
  }
  if (selectedByFace.size === 0) return faces.map((face) => ({ ...face }))

  const next = new Map<string, T>()
  for (const face of faces) {
    next.set(face.face_key, { ...face, tile_colors: face.tile_colors ? { ...face.tile_colors } : face.tile_colors })
  }

  for (const [faceKey, selected] of selectedByFace) {
    const rows = wallFaceRowCount(faceKey, cells, platform)
    const existing = next.get(faceKey)
    const coversAll = rows > 0 && Array.from({ length: rows }, (_, v) => selected.has(wallTileKey(0, v))).every(Boolean)
    if (hex && coversAll) {
      next.set(faceKey, {
        ...(existing ?? { face_key: faceKey, origin: "upload", status: "ready" }),
        solid_color: hex,
        tile_colors: {},
        url: null,
        thumb_url: null,
        download_url: null,
        status: "ready",
      } as T)
      continue
    }

    const colors: Record<string, string> = {}
    const stored = existing?.tile_colors
    if (stored && Object.keys(stored).length > 0) {
      for (const [key, value] of Object.entries(stored)) {
        if (value) colors[key] = value
      }
    } else if (existing?.solid_color) {
      for (let v = 0; v < rows; v += 1) colors[wallTileKey(0, v)] = existing.solid_color
    }
    if (hex) {
      for (const key of selected) colors[key] = hex
    } else {
      for (const key of selected) delete colors[key]
    }

    const hasImage = Boolean(existing?.url || existing?.download_url || existing?.thumb_url)
    if (Object.keys(colors).length === 0 && !hasImage) {
      next.delete(faceKey)
      continue
    }
    next.set(faceKey, {
      ...(existing ?? { face_key: faceKey, origin: "upload", status: "ready" }),
      solid_color: null,
      tile_colors: colors,
      status: existing?.status === "pending" ? existing.status : "ready",
    } as T)
  }

  return [...next.values()]
}

export function faceSideFromKey(faceKey: string): string | null {
  const match = faceKey.match(/:(north|south|east|west|top|bottom)$/)
  return match?.[1] ?? null
}

export function parseCellFaceKey(faceKey: string): { x: number; z: number; face: CellFace } | null {
  const match = faceKey.match(CELL_FACE_RE)
  if (!match) return null
  return { x: Number(match[1]), z: Number(match[2]), face: match[3] as CellFace }
}

/** Every UV tile covering a cell face (wall stack or top/bottom FACE_GRID). */
export function faceAllTiles(faceKey: string, cells: ReadonlyArray<SceneCell>, platform: ScenePlatform): FaceTile[] {
  const parsed = parseCellFaceKey(faceKey)
  if (!parsed) return []
  const { x, z, face } = parsed
  if (face === "top" || face === "bottom") {
    const tiles: FaceTile[] = []
    for (let v = 0; v < FACE_GRID; v += 1) {
      for (let u = 0; u < FACE_GRID; u += 1) {
        tiles.push({ faceKey, u, v })
      }
    }
    return tiles
  }
  const height = cells.find((cell) => cell.x === x && cell.z === z)?.height ?? 0
  if (height <= 0) return []
  const sectorM = sectorSizeMeters(platform)
  const { cols, rows } = faceTileGrid(height * sectorM, sectorM)
  const tiles: FaceTile[] = []
  for (let v = 0; v < rows; v += 1) {
    for (let u = 0; u < cols; u += 1) {
      tiles.push({ faceKey, u, v })
    }
  }
  return tiles
}

/**
 * Contiguous vertical wall run sharing the same side: north/south along x (fixed z),
 * east/west along z (fixed x). Stops at height <= 0 or platform edge. Top/bottom: single key.
 */
export function contiguousWallFaceKeys(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): string[] {
  const parsed = parseCellFaceKey(faceKey)
  if (!parsed) return []
  const { x, z, face } = parsed
  if (face === "top" || face === "bottom") return [faceKey]
  const heightAt = new Map(cells.map((cell) => [`${cell.x}:${cell.z}`, cell.height] as const))
  const solid = (cx: number, cz: number) => (heightAt.get(`${cx}:${cz}`) ?? 0) > 0
  if (!solid(x, z)) return []
  if (face === "north" || face === "south") {
    let x0 = x
    let x1 = x
    while (x0 > 0 && solid(x0 - 1, z)) x0 -= 1
    while (x1 < platform.sectors_x - 1 && solid(x1 + 1, z)) x1 += 1
    const keys: string[] = []
    for (let cx = x0; cx <= x1; cx += 1) keys.push(cellFaceKey(cx, z, face))
    return keys
  }
  if (face === "east" || face === "west") {
    let z0 = z
    let z1 = z
    while (z0 > 0 && solid(x, z0 - 1)) z0 -= 1
    while (z1 < platform.sectors_z - 1 && solid(x, z1 + 1)) z1 += 1
    const keys: string[] = []
    for (let cz = z0; cz <= z1; cz += 1) keys.push(cellFaceKey(x, cz, face))
    return keys
  }
  return [faceKey]
}

/** Canonical (first) face_key of a contiguous wall run — used for one material per wall. */
export function wallRunPrimaryFaceKey(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): string | null {
  const run = contiguousWallFaceKeys(faceKey, cells, platform)
  return run[0] ?? null
}

/** Precompute wall runs once — avoids O(W²) contiguousWallFaceKeys during rebuild. */
export type WallRunIndex = {
  byFaceKey: Map<string, string[]>
  runs: string[][]
}

export function buildWallRunIndex(cells: ReadonlyArray<SceneCell>, platform: ScenePlatform): WallRunIndex {
  const byFaceKey = new Map<string, string[]>()
  const runs: string[][] = []
  const wallFaces: CellFace[] = ["north", "south", "east", "west"]
  for (const cell of cells) {
    if (cell.height <= 0) continue
    for (const face of wallFaces) {
      const seed = cellFaceKey(cell.x, cell.z, face)
      if (byFaceKey.has(seed)) continue
      const run = contiguousWallFaceKeys(seed, cells, platform)
      if (run.length === 0) continue
      for (const key of run) byFaceKey.set(key, run)
      runs.push(run)
    }
  }
  return { byFaceKey, runs }
}

/**
 * Face keys that should receive generate/upload/library.
 * Wall mode: one primary per distinct contiguous run. Tiles: unique keys as selected.
 */
export function wallMediaFaceKeys(
  faceKeys: ReadonlyArray<string>,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
  mode: "wall" | "tiles",
): string[] {
  if (mode === "tiles") {
    return [...new Set(faceKeys)]
  }
  const primaries: string[] = []
  const seenRuns = new Set<string>()
  for (const faceKey of faceKeys) {
    const run = contiguousWallFaceKeys(faceKey, cells, platform)
    if (run.length === 0) continue
    const runId = run.join(",")
    if (seenRuns.has(runId)) continue
    seenRuns.add(runId)
    primaries.push(run[0])
  }
  return primaries
}

/** Sibling face keys in the same wall run excluding the primary (for clear-after-write). */
export function wallRunSiblingFaceKeys(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): string[] {
  const run = contiguousWallFaceKeys(faceKey, cells, platform)
  if (run.length <= 1) return []
  const primary = run[0]
  return run.filter((key) => key !== primary)
}

/** Horizontal UV slice of a wall-run texture for this cell face (stretch across the wall). */
export function wallRunTextureSlice(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): { index: number; count: number } | null {
  const run = contiguousWallFaceKeys(faceKey, cells, platform)
  if (run.length === 0) return null
  const index = run.indexOf(faceKey)
  if (index < 0) return null
  return { index, count: run.length }
}

/**
 * 2D UV atlas rect for one voxel face on a wall run.
 * U = cell along contiguous run; V = voxel row in the column stack.
 * Returns null when the face is a full (non-sliced) frame.
 */
export function wallFaceTextureRect(
  faceKey: string,
  rowV: number,
  rows: number,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): { uIndex: number; uCount: number; vIndex: number; vCount: number } | null {
  const parsed = parseCellFaceKey(faceKey)
  if (!parsed || parsed.face === "top" || parsed.face === "bottom") return null
  const uSlice = wallRunTextureSlice(faceKey, cells, platform)
  if (!uSlice) return null
  const vCount = Math.max(1, Math.floor(rows))
  const vIndex = Math.max(0, Math.min(vCount - 1, Math.floor(rowV)))
  if (uSlice.count <= 1 && vCount <= 1) return null
  return {
    uIndex: uSlice.index,
    uCount: uSlice.count,
    vIndex,
    vCount,
  }
}

export type WallSurfaceMeshData = {
  positions: number[]
  uvs: number[]
  indices: number[]
  quadCount: number
}

/**
 * Merged quads for one contiguous wall run: UV 0..1 across run width and max height.
 * Skips carved voxel faces so dig openings stay empty.
 */
export function wallRunSurfaceMeshData(args: {
  runFaceKeys: ReadonlyArray<string>
  cells: ReadonlyArray<SceneCell>
  platform: ScenePlatform
  carved: ReadonlySet<string>
  /** Skip these faces' quads. UV stays indexed on the full run so a cut-out color sector does not restretch the image. */
  omitFaceKeys?: ReadonlySet<string>
  /** Skip one row (`faceKey:v`) but keep UV v on the full column height. */
  omitTiles?: ReadonlySet<string>
  /** Emit only this row. UV v still uses the full column height. */
  onlyV?: number
  outwardEpsilon?: number
}): WallSurfaceMeshData | null {
  const { runFaceKeys, cells, platform, carved } = args
  if (runFaceKeys.length === 0) return null
  const first = parseCellFaceKey(runFaceKeys[0] ?? "")
  if (!first || first.face === "top" || first.face === "bottom") return null
  const face = first.face
  const heightAt = new Map(cells.map((cell) => [`${cell.x}:${cell.z}`, cell.height] as const))
  let maxHeight = 0
  for (const key of runFaceKeys) {
    const parsed = parseCellFaceKey(key)
    if (!parsed) continue
    maxHeight = Math.max(maxHeight, heightAt.get(`${parsed.x}:${parsed.z}`) ?? 0)
  }
  if (maxHeight <= 0) return null
  const sectorM = sectorSizeMeters(platform)
  const span = Math.max(platform.sectors_x, platform.sectors_z) * sectorM
  const { rows } = faceTileGrid(maxHeight * sectorM, sectorM)
  if (rows <= 0) return null
  const runLen = runFaceKeys.length
  const eps = args.outwardEpsilon ?? 0.002
  const positions: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  let quadCount = 0

  const pushQuad = (
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    cx: number,
    cy: number,
    cz: number,
    dx: number,
    dy: number,
    dz: number,
    ua: number,
    va: number,
    ub: number,
    vb: number,
    uc: number,
    vc: number,
    ud: number,
    vd: number,
  ) => {
    const base = positions.length / 3
    positions.push(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz)
    uvs.push(ua, va, ub, vb, uc, vc, ud, vd)
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
    quadCount += 1
  }

  for (let cellIndex = 0; cellIndex < runLen; cellIndex += 1) {
    const faceKey = runFaceKeys[cellIndex]
    if (!faceKey || args.omitFaceKeys?.has(faceKey)) continue
    const parsed = parseCellFaceKey(faceKey)
    if (!parsed) continue
    const { x, z } = parsed
    const height = heightAt.get(`${x}:${z}`) ?? 0
    if (height <= 0) continue
    const cellRows = faceTileGrid(height * sectorM, sectorM).rows
    const baseY = cellStackBaseY(height, sectorM, span)
    const cx = (x + 0.5 - platform.sectors_x / 2) * sectorM
    const cz = (z + 0.5 - platform.sectors_z / 2) * sectorM
    const half = sectorM / 2
    // Run grows with +X (N/S) or +Z (E/W). On north/east that axis points screen-left,
    // so atlas U is indexed from the end of the run to keep source left→right on screen.
    const reverseAlongScreen = face === "north" || face === "east"
    const atlasIndex = reverseAlongScreen ? runLen - 1 - cellIndex : cellIndex
    const uLo = atlasIndex / runLen
    const uHi = (atlasIndex + 1) / runLen
    for (let v = 0; v < cellRows; v += 1) {
      if (args.onlyV != null && v !== args.onlyV) continue
      if (args.omitTiles?.has(`${faceKey}:${v}`)) continue
      if (carved.has(carvedVoxelKey(x, z, v))) continue
      const y0 = baseY + v * sectorM
      const y1 = y0 + sectorM
      const v0 = v / rows
      const v1 = (v + 1) / rows
      if (face === "north") {
        const zf = cz - half - eps
        const x0 = cx - half
        const x1 = cx + half
        // Exterior +Z: screen-left = +X (x1) → uLo; screen-right = −X (x0) → uHi.
        pushQuad(x0, y0, zf, x0, y1, zf, x1, y1, zf, x1, y0, zf, uHi, v0, uHi, v1, uLo, v1, uLo, v0)
      } else if (face === "south") {
        const zf = cz + half + eps
        const x0 = cx - half
        const x1 = cx + half
        // Exterior −Z: screen-left = −X (x0) → uLo; screen-right = +X (x1) → uHi.
        pushQuad(x0, y0, zf, x1, y0, zf, x1, y1, zf, x0, y1, zf, uLo, v0, uHi, v0, uHi, v1, uLo, v1)
      } else if (face === "east") {
        const xf = cx + half + eps
        const z0 = cz - half
        const z1 = cz + half
        // Exterior −X: screen-left = +Z (z1) → uLo; screen-right = −Z (z0) → uHi.
        pushQuad(xf, y0, z0, xf, y1, z0, xf, y1, z1, xf, y0, z1, uHi, v0, uHi, v1, uLo, v1, uLo, v0)
      } else {
        const xf = cx - half - eps
        const z0 = cz - half
        const z1 = cz + half
        // Exterior +X: screen-left = −Z (z0) → uLo; screen-right = +Z (z1) → uHi.
        pushQuad(xf, y0, z1, xf, y1, z1, xf, y1, z0, xf, y0, z0, uHi, v0, uHi, v1, uLo, v1, uLo, v0)
      }
    }
  }

  if (quadCount === 0) return null
  return { positions, uvs, indices, quadCount }
}

/** Cell keys (`x:z`) for the contiguous wall run containing `faceKey`. */
export function wallRunCellKeys(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): Set<string> {
  const keys = new Set<string>()
  for (const key of contiguousWallFaceKeys(faceKey, cells, platform)) {
    const parsed = parseCellFaceKey(key)
    if (parsed) keys.add(cellKey(parsed.x, parsed.z))
  }
  return keys
}

/** Union of wall-run cells for every distinct faceKey in a tile selection. */
export function wallRunCellKeysFromTiles(
  tiles: ReadonlyArray<FaceTile>,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): Set<string> {
  const keys = new Set<string>()
  const seenFaces = new Set<string>()
  for (const tile of tiles) {
    if (seenFaces.has(tile.faceKey)) continue
    seenFaces.add(tile.faceKey)
    for (const cell of wallRunCellKeys(tile.faceKey, cells, platform)) keys.add(cell)
  }
  return keys
}

/** All UV tiles for a contiguous wall selection (or one top/bottom face). */
export function wallSelectionTiles(
  faceKey: string,
  cells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): FaceTile[] {
  const tiles: FaceTile[] = []
  for (const key of contiguousWallFaceKeys(faceKey, cells, platform)) {
    tiles.push(...faceAllTiles(key, cells, platform))
  }
  return tiles
}

/** Rectangle of face tiles across adjacent sector faces on the same side (like sectorRectangle). */
export function faceTileRectangle(anchor: FaceTile, current: FaceTile): FaceTile[] {
  const a = parseCellFaceKey(anchor.faceKey)
  const c = parseCellFaceKey(current.faceKey)
  if (!a || !c || a.face !== c.face) {
    if (anchor.faceKey !== current.faceKey) return [{ ...current }]
    return sectorRectangle({ x: anchor.u, z: anchor.v }, { x: current.u, z: current.v }).map((cell) => ({
      faceKey: anchor.faceKey,
      u: cell.x,
      v: cell.z,
    }))
  }
  const face = a.face
  const u0 = Math.min(anchor.u, current.u)
  const u1 = Math.max(anchor.u, current.u)
  const v0 = Math.min(anchor.v, current.v)
  const v1 = Math.max(anchor.v, current.v)
  const tiles: FaceTile[] = []
  if (face === "north" || face === "south") {
    if (a.z !== c.z) {
      return sectorRectangle({ x: anchor.u, z: anchor.v }, { x: current.u, z: current.v }).map((cell) => ({
        faceKey: current.faceKey,
        u: cell.x,
        v: cell.z,
      }))
    }
    const x0 = Math.min(a.x, c.x)
    const x1 = Math.max(a.x, c.x)
    for (let x = x0; x <= x1; x += 1) {
      for (let v = v0; v <= v1; v += 1) {
        for (let u = u0; u <= u1; u += 1) {
          tiles.push({ faceKey: cellFaceKey(x, a.z, face), u, v })
        }
      }
    }
    return tiles
  }
  if (face === "east" || face === "west") {
    if (a.x !== c.x) {
      return sectorRectangle({ x: anchor.u, z: anchor.v }, { x: current.u, z: current.v }).map((cell) => ({
        faceKey: current.faceKey,
        u: cell.x,
        v: cell.z,
      }))
    }
    const z0 = Math.min(a.z, c.z)
    const z1 = Math.max(a.z, c.z)
    for (let z = z0; z <= z1; z += 1) {
      for (let v = v0; v <= v1; v += 1) {
        for (let u = u0; u <= u1; u += 1) {
          tiles.push({ faceKey: cellFaceKey(a.x, z, face), u, v })
        }
      }
    }
    return tiles
  }
  return sectorRectangle({ x: anchor.u, z: anchor.v }, { x: current.u, z: current.v }).map((cell) => ({
    faceKey: anchor.faceKey,
    u: cell.x,
    v: cell.z,
  }))
}

export function platformError(platform: ScenePlatform): PlatformErrorCode | null {
  if (!Number.isInteger(platform.sectors_x) || platform.sectors_x < SECTORS_MIN || platform.sectors_x > SECTORS_MAX) {
    return "sectors_x"
  }
  if (!Number.isInteger(platform.sectors_z) || platform.sectors_z < SECTORS_MIN || platform.sectors_z > SECTORS_MAX) {
    return "sectors_z"
  }
  if (platform.unit !== "cm" && platform.unit !== "m") return "unit"
  const size = platform.sector_size
  if (!Number.isFinite(size)) return "sector_size"
  if (platform.unit === "cm" && (size < SECTOR_SIZE_CM_MIN || size > SECTOR_SIZE_CM_MAX)) return "sector_size"
  if (platform.unit === "m" && (size < SECTOR_SIZE_M_MIN || size > SECTOR_SIZE_M_MAX)) return "sector_size"
  const meters = sectorSizeMeters(platform)
  if (platform.sectors_x * meters > PLATFORM_SIDE_MAX_M || platform.sectors_z * meters > PLATFORM_SIDE_MAX_M) {
    return "platform_side"
  }
  return null
}

export function cellKey(x: number, z: number): string {
  return `${x}:${z}`
}

/**
 * Perimeter wall cells for create-dialog presets. One sector thick along each selected side;
 * corners are not duplicated. Empty sides or non-positive height → [].
 */
export function seedPerimeterWallCells(
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
  sides: ReadonlySet<WallSide>,
  height: number,
): SceneCell[] {
  const sx = platform.sectors_x
  const sz = platform.sectors_z
  if (!(sx > 0) || !(sz > 0) || sides.size === 0) return []
  const h = Math.min(HEIGHT_MAX, Math.max(0, Math.round(height)))
  if (h <= 0) return []
  const map = new Map<string, SceneCell>()
  const add = (x: number, z: number) => {
    map.set(cellKey(x, z), { x, z, height: h })
  }
  if (sides.has("north")) {
    for (let x = 0; x < sx; x += 1) add(x, 0)
  }
  if (sides.has("south")) {
    for (let x = 0; x < sx; x += 1) add(x, sz - 1)
  }
  if (sides.has("west")) {
    for (let z = 0; z < sz; z += 1) add(0, z)
  }
  if (sides.has("east")) {
    for (let z = 0; z < sz; z += 1) add(sx - 1, z)
  }
  return [...map.values()].sort((a, b) => a.z - b.z || a.x - b.x)
}

/** Sector indices with height ≤ 0 (missing cell = floor). */
export function walkableFloorSectors(
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
  cells: ReadonlyArray<SceneCell>,
): { x: number; z: number }[] {
  const heights = new Map(cells.map((cell) => [cellKey(cell.x, cell.z), cell.height]))
  const out: { x: number; z: number }[] = []
  for (let z = 0; z < platform.sectors_z; z += 1) {
    for (let x = 0; x < platform.sectors_x; x += 1) {
      if ((heights.get(cellKey(x, z)) ?? 0) <= 0) out.push({ x, z })
    }
  }
  return out
}

/** World XZ of the sector cell center (platform centered on origin). */
export function sectorWorldCenter(
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z" | "sector_size" | "unit">,
  x: number,
  z: number,
): [number, number] {
  const sectorM = sectorSizeMeters(platform)
  return [(x + 0.5 - platform.sectors_x / 2) * sectorM, (z + 0.5 - platform.sectors_z / 2) * sectorM]
}

/** Sector index under a world XZ point, or null if outside the platform. */
export function worldToSectorIndex(
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z" | "sector_size" | "unit">,
  worldX: number,
  worldZ: number,
): { x: number; z: number } | null {
  const sectorM = sectorSizeMeters(platform)
  if (!(sectorM > 0)) return null
  const x = Math.floor(worldX / sectorM + platform.sectors_x / 2)
  const z = Math.floor(worldZ / sectorM + platform.sectors_z / 2)
  if (x < 0 || z < 0 || x >= platform.sectors_x || z >= platform.sectors_z) return null
  return { x, z }
}

export function sectorRectangle(
  anchor: { x: number; z: number },
  current: { x: number; z: number },
): { x: number; z: number }[] {
  const x0 = Math.min(anchor.x, current.x)
  const x1 = Math.max(anchor.x, current.x)
  const z0 = Math.min(anchor.z, current.z)
  const z1 = Math.max(anchor.z, current.z)
  const cells: { x: number; z: number }[] = []
  for (let z = z0; z <= z1; z += 1) {
    for (let x = x0; x <= x1; x += 1) cells.push({ x, z })
  }
  return cells
}

/** Cells committed when a floor marquee is released. A cell under a character or prop is included. */
export function sectorPaintCells(
  anchor: { x: number; z: number },
  current: { x: number; z: number },
): { x: number; z: number }[] {
  return sectorRectangle(anchor, current)
}

export function applyHeightDelta(
  cells: SceneCell[],
  selection: ReadonlyArray<{ x: number; z: number }>,
  delta: number,
): SceneCell[] {
  const map = new Map<string, SceneCell>()
  for (const cell of cells) map.set(cellKey(cell.x, cell.z), cell)
  for (const sector of selection) {
    const key = cellKey(sector.x, sector.z)
    const current = map.get(key)?.height ?? 0
    const height = Math.min(HEIGHT_MAX, Math.max(0, current + delta))
    if (Math.abs(height) < 0.000001) map.delete(key)
    else map.set(key, { x: sector.x, z: sector.z, height })
  }
  return [...map.values()].sort((a, b) => a.z - b.z || a.x - b.x)
}

/** True if any selected sector is floor (missing / height ≤ 0). */
export function selectionHasFloorCell(
  cells: ReadonlyArray<SceneCell>,
  selection: ReadonlyArray<{ x: number; z: number }>,
): boolean {
  const heights = new Map(cells.map((cell) => [cellKey(cell.x, cell.z), cell.height]))
  return selection.some((sector) => (heights.get(cellKey(sector.x, sector.z)) ?? 0) <= 0)
}

export function applyOpeningDepthDelta(
  openings: SceneOpening[],
  faceKey: string,
  selection: ReadonlyArray<{ u: number; v: number }>,
  delta: number,
): SceneOpening[] {
  const map = new Map<string, SceneOpening>()
  for (const opening of openings) map.set(`${opening.face_key}:${opening.u}:${opening.v}`, opening)
  for (const cell of selection) {
    const key = `${faceKey}:${cell.u}:${cell.v}`
    const current = map.get(key)?.depth ?? 0
    const depth = Math.min(OPENING_DEPTH_MAX, Math.max(OPENING_DEPTH_MIN, current + delta))
    if (Math.abs(depth) <= 0.000001) map.delete(key)
    else map.set(key, { face_key: faceKey, u: cell.u, v: cell.v, depth })
  }
  return [...map.values()].sort((a, b) => a.face_key.localeCompare(b.face_key) || a.v - b.v || a.u - b.u)
}

/** How many cubes can be carved inward from this face row before empty / edge. */
export function digTunnelLength(
  faceKey: string,
  v: number,
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
): number {
  const parsed = parseCellFaceKey(faceKey)
  if (!parsed) return 0
  const step = inwardStep(parsed.face)
  if (!step) return 0
  const heights = new Map<string, number>()
  for (const cell of cells) heights.set(cellKey(cell.x, cell.z), cell.height)
  let length = 0
  for (let i = 0; i < OPENING_DEPTH_MAX; i += 1) {
    const cx = parsed.x + step.dx * i
    const cz = parsed.z + step.dz * i
    if (cx < 0 || cz < 0 || cx >= platform.sectors_x || cz >= platform.sectors_z) break
    const height = heights.get(cellKey(cx, cz)) ?? 0
    const rows = Math.max(0, Math.floor(height + 1e-9))
    if (v < 0 || v >= rows) break
    length += 1
  }
  return length
}

/** Dig opening that has carved all available material (no far face left). */
export function openingDigThrough(
  opening: SceneOpening,
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
): boolean {
  if (opening.depth <= 0) return false
  const length = digTunnelLength(opening.face_key, opening.v, cells, platform)
  return opening.depth + 1e-9 >= length
}

/** Cap dig depths to tunnel length; drop digs with no material. */
export function clampDigOpenings(
  openings: ReadonlyArray<SceneOpening>,
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
): SceneOpening[] {
  const next: SceneOpening[] = []
  for (const opening of openings) {
    if (opening.depth <= 0) {
      next.push(opening)
      continue
    }
    const length = digTunnelLength(opening.face_key, opening.v, cells, platform)
    if (length <= 0) continue
    const depth = Math.min(opening.depth, length)
    next.push(depth === opening.depth ? opening : { ...opening, depth })
  }
  return next.sort((a, b) => a.face_key.localeCompare(b.face_key) || a.v - b.v || a.u - b.u)
}

/** Inward grid step from a vertical face (dig tunnel direction). */
export function inwardStep(face: CellFace): { dx: number; dz: number } | null {
  if (face === "south") return { dx: 0, dz: -1 }
  if (face === "north") return { dx: 0, dz: 1 }
  if (face === "east") return { dx: -1, dz: 0 }
  if (face === "west") return { dx: 1, dz: 0 }
  return null
}

/** Outward grid step (extrude / protrude direction). */
export function outwardStep(face: CellFace): { dx: number; dz: number } | null {
  const inn = inwardStep(face)
  if (!inn) return null
  return { dx: -inn.dx || 0, dz: -inn.dz || 0 }
}

/** Near-zero: keep default ←=out / →=in (normal almost along view). */
export const FACE_SHIFT_ARROW_FLIP_EPS = 1e-3

/**
 * True when wall outward points to the right of the camera on XZ —
 * then toolbar should put extrude on → (and intrude on ←).
 */
export function faceShiftArrowFlip(
  outwardXZ: { dx: number; dz: number },
  cameraRightXZ: { x: number; z: number },
): boolean {
  const dot = outwardXZ.dx * cameraRightXZ.x + outwardXZ.dz * cameraRightXZ.z
  if (Math.abs(dot) < FACE_SHIFT_ARROW_FLIP_EPS) return false
  return dot > 0
}

export type WallFaceCellShiftResult = {
  cells: SceneCell[]
  /** Face keys to select after the shift (may be empty). */
  nextFaceKeys: string[]
  /** Face keys whose columns were touched — scrub openings on these. */
  affectedFaceKeys: string[]
  /** Source face_key → destination face_key for columns that landed on a new face. */
  keyMap: Map<string, string>
  /** Dig sources with no inward neighbor — drop materials on these keys. */
  orphanFaceKeys: string[]
}

/**
 * Wall-mode ←→: mutate sector columns instead of openings.
 * direction < 0 = extrude outward (add/raise neighbor); direction > 0 = dig (clear face columns).
 */
export function applyWallFaceCellShift(
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
  faceTiles: ReadonlyArray<FaceTile>,
  direction: 1 | -1,
): WallFaceCellShiftResult {
  const map = new Map<string, SceneCell>()
  for (const cell of cells) map.set(cellKey(cell.x, cell.z), { ...cell })

  const faces = new Map<string, { x: number; z: number; face: CellFace }>()
  for (const tile of faceTiles) {
    const parsed = parseCellFaceKey(tile.faceKey)
    if (!parsed || parsed.face === "top" || parsed.face === "bottom") continue
    faces.set(tile.faceKey, parsed)
  }

  const affectedFaceKeys: string[] = []
  const nextFaceKeys: string[] = []
  const keyMap = new Map<string, string>()
  const orphanFaceKeys: string[] = []
  const inBounds = (x: number, z: number) => x >= 0 && z >= 0 && x < platform.sectors_x && z < platform.sectors_z

  if (direction < 0) {
    // Extrude outward: raise/create neighbor to at least source height.
    for (const { x, z, face } of faces.values()) {
      const step = outwardStep(face)
      if (!step) continue
      const srcH = map.get(cellKey(x, z))?.height ?? 0
      if (srcH <= 0) continue
      const nx = x + step.dx
      const nz = z + step.dz
      if (!inBounds(nx, nz)) continue
      const key = cellKey(nx, nz)
      const cur = map.get(key)?.height ?? 0
      const height = Math.min(HEIGHT_MAX, Math.max(cur, srcH))
      map.set(key, { x: nx, z: nz, height })
      const fromKey = cellFaceKey(x, z, face)
      const toKey = cellFaceKey(nx, nz, face)
      affectedFaceKeys.push(fromKey, toKey)
      nextFaceKeys.push(toKey)
      keyMap.set(fromKey, toKey)
    }
  } else {
    // Dig inward: clear the face columns; select next solid cell inward if any.
    for (const { x, z, face } of faces.values()) {
      const step = inwardStep(face)
      if (!step) continue
      const srcKey = cellKey(x, z)
      if ((map.get(srcKey)?.height ?? 0) <= 0) continue
      map.delete(srcKey)
      const fromKey = cellFaceKey(x, z, face)
      affectedFaceKeys.push(fromKey)
      const nx = x + step.dx
      const nz = z + step.dz
      if (inBounds(nx, nz) && (map.get(cellKey(nx, nz))?.height ?? 0) > 0) {
        const toKey = cellFaceKey(nx, nz, face)
        nextFaceKeys.push(toKey)
        affectedFaceKeys.push(toKey)
        keyMap.set(fromKey, toKey)
      } else {
        orphanFaceKeys.push(fromKey)
      }
    }
  }

  const uniqueNext = [...new Set(nextFaceKeys)]
  const uniqueAffected = [...new Set(affectedFaceKeys)]
  return {
    cells: [...map.values()].sort((a, b) => a.z - b.z || a.x - b.x),
    nextFaceKeys: uniqueNext,
    affectedFaceKeys: uniqueAffected,
    keyMap,
    orphanFaceKeys: [...new Set(orphanFaceKeys)],
  }
}

export type FaceKeyRemap = { from: string; to: string }

/** Remap scene face materials after a wall column shift; drop orphans / collapse collisions on `to`. */
export function remapSceneFacesForWallShift<T extends { face_key: string }>(
  faces: ReadonlyArray<T>,
  keyMap: ReadonlyMap<string, string>,
  orphanFaceKeys: ReadonlyArray<string>,
  nextCells: ReadonlyArray<SceneCell>,
  platform: ScenePlatform,
): { faces: T[]; mappings: FaceKeyRemap[] } {
  const orphan = new Set(orphanFaceKeys)
  const usedTo = new Set<string>()
  const mappings: FaceKeyRemap[] = []
  const next: T[] = []
  for (const face of faces) {
    if (orphan.has(face.face_key)) continue
    const mapped = keyMap.get(face.face_key)
    if (!mapped) {
      next.push(face)
      continue
    }
    const to = wallRunPrimaryFaceKey(mapped, nextCells, platform) ?? mapped
    if (usedTo.has(to)) continue
    usedTo.add(to)
    if (face.face_key !== to) mappings.push({ from: face.face_key, to })
    next.push({ ...face, face_key: to })
  }
  return { faces: next, mappings }
}

/** Drop openings whose face_key is in the set (any u/v). */
export function scrubOpeningsForFaceKeys(
  openings: ReadonlyArray<SceneOpening>,
  faceKeys: ReadonlyArray<string>,
): SceneOpening[] {
  if (faceKeys.length === 0) return [...openings]
  const drop = new Set(faceKeys)
  return openings.filter((opening) => !drop.has(opening.face_key))
}

export function carvedVoxelKey(x: number, z: number, v: number): string {
  return `${x}:${z}:${v}`
}

/**
 * Dig openings (depth > 0) carve one cube per sector step along the inward normal.
 * Stops at platform edge or when the column has no cube at row v.
 */
export function carvedVoxels(
  openings: ReadonlyArray<SceneOpening>,
  cells: ReadonlyArray<SceneCell>,
  platform: Pick<ScenePlatform, "sectors_x" | "sectors_z">,
): Set<string> {
  const heights = new Map<string, number>()
  for (const cell of cells) heights.set(cellKey(cell.x, cell.z), cell.height)
  const carved = new Set<string>()
  for (const opening of openings) {
    if (opening.depth <= 0) continue
    const parsed = parseCellFaceKey(opening.face_key)
    if (!parsed) continue
    const step = inwardStep(parsed.face)
    if (!step) continue
    const steps = Math.min(OPENING_DEPTH_MAX, Math.max(0, Math.floor(opening.depth + 1e-9)))
    for (let i = 0; i < steps; i += 1) {
      const cx = parsed.x + step.dx * i
      const cz = parsed.z + step.dz * i
      if (cx < 0 || cz < 0 || cx >= platform.sectors_x || cz >= platform.sectors_z) break
      const height = heights.get(cellKey(cx, cz)) ?? 0
      const rows = Math.max(0, Math.floor(height + 1e-9))
      if (opening.v < 0 || opening.v >= rows) break
      carved.add(carvedVoxelKey(cx, cz, opening.v))
    }
  }
  return carved
}

export function cellFaceKey(x: number, z: number, face: CellFace): string {
  return `cell:${x}:${z}:${face}`
}

/** Top face keys for selected floor/raised sectors (solid paint target). */
export function sectorTopFaceKeys(sectors: ReadonlyArray<{ x: number; z: number }>): string[] {
  return sectors.map((sector) => cellFaceKey(sector.x, sector.z, "top"))
}

export function objectFaceKey(uid: string, face: string): string {
  return `obj:${uid}:${face}`
}

export function validFaceKey(key: string): boolean {
  return CELL_FACE_RE.test(key) || OBJECT_FACE_RE.test(key)
}
